import React from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Tag,
  HStack,
  Stack,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  useColorModeValue,
} from "@chakra-ui/react";

import ImageSlider from "./ImageSlider";

/**
 * ProgramAll.jsx (Professional TRAVERSAL style)
 * - NO content changes: all text/images/links are kept as-is.
 * - Single file (no extra components imported except your existing ImageSlider).
 * - Removes Aphrodite + <hr/> and replaces with modern Chakra layout.
 * - Matches the “SITE-CAV program” vibe: hero header + clean section cards.
 */

export default function TrainingTrackMerged() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);

  return (
    <Box>
      <TraversalProgramHeader />

      <ProgramSection title="FORMATION">
        <TrainingSection isMobile={isMobile} />
      </ProgramSection>

      <ProgramSection title="HACKATHON">
        <HackathonSection isMobile={isMobile} />
      </ProgramSection>

      <ProgramSection title="STAGES">
        <InternshipSection isMobile={isMobile} />
      </ProgramSection>

      <ProgramSection title="ÉCHANGES INTERNATIONAUX">
        <InternationalExchangesSection isMobile={isMobile} />
      </ProgramSection>

      <ProgramSection title="CONFÉRENCES">
        <ConferencesAndApplySection isMobile={isMobile} />
      </ProgramSection>

      <Box pb={{ base: 10, md: 14 }} />
    </Box>
  );
}

/* =========================================================
   HEADER (like SITE-CAV, TRAVERSAL colors)
========================================================= */
function TraversalProgramHeader() {
  const accent = "#06B6D4";
  const accent2 = "#22D3EE";

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");

  const heroBg = useColorModeValue(
    "radial-gradient(1000px 420px at 12% 0%, rgba(6,182,212,0.18), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(34,211,238,0.14), transparent 60%), linear-gradient(180deg, rgba(2,6,23,0.02), rgba(2,6,23,0.00))",
    "radial-gradient(1000px 420px at 12% 0%, rgba(6,182,212,0.20), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(34,211,238,0.14), transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
  );

  return (
    <Box
      pt={{ base: 7, md: 10 }}
      pb={{ base: 8, md: 10 }}
      bg={heroBg}
      borderBottom="1px solid"
      borderColor={border}
    >
      <Container maxW="1200px">
        <HStack spacing={2} flexWrap="wrap">
          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
            border="1px solid"
            borderColor={border}
            fontWeight="800"
          >
            University of Ottawa
          </Tag>

          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("rgba(6,182,212,0.12)", "rgba(6,182,212,0.18)")}
            color={useColorModeValue("#0E7490", "#67E8F9")}
            fontWeight="900"
          >
            TRAVERSAL
          </Tag>

        </HStack>

        <Heading
          mt={4}
          fontSize={{ base: "3xl", md: "4xl" }}
          color={titleColor}
          letterSpacing="tight"
          lineHeight={1.05}
        >
          <Box as="span" bgClip="text" bgGradient={`linear(to-r, ${accent}, ${accent2})`}>
            TRAVERSAL
          </Box>{" "}
          Programme
        </Heading>

        <Text mt={3} color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="90ch" lineHeight={1.8}>
          Formations, hackathons, stages, échanges internationaux, conférences et conseils pour les candidatures des stagiaires du programme CREATE.
        </Text>
      </Container>
    </Box>
  );
}

/* =========================================================
   SECTION SHELL (consistent rhythm)
========================================================= */
function ProgramSection({ title, children }) {
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const bg = useColorModeValue("white", "#0B1220");

  const accent = "#06B6D4";
  const accent2 = "#22D3EE";

  return (
    <Box bg={bg}>
      <Container maxW="1200px" py={{ base: 4, md: 6 }}>
        <Stack spacing={3} textAlign={{ base: "center", md: "left" }}>
          <Heading fontSize={{ base: "2xl", md: "3xl" }} color={titleColor} letterSpacing="tight" fontWeight="900">
            {title}
          </Heading>

          <Box
            mx={{ base: "auto", md: 0 }}
            w={{ base: "110px", md: "140px" }}
            h="3px"
            borderRadius="999px"
            bg={`linear-gradient(90deg, ${accent} 0%, ${accent2} 45%, rgba(34,211,238,0) 100%)`}
          />
        </Stack>

        <Box mt={{ base: 7, md: 9 }}>
          <Box
            border="1px solid"
            borderColor={border}
            borderRadius="26px"
            bg={useColorModeValue("rgba(255,255,255,0.85)", "rgba(255,255,255,0.03)")}
            boxShadow={useColorModeValue("0 14px 44px rgba(0,0,0,0.08)", "none")}
            p={{ base: 5, md: 7 }}
          >
            {children}
          </Box>
        </Box>

        <Box mt={{ base: 10, md: 12 }}>
          <Divider borderColor={border} />
        </Box>
      </Container>
    </Box>
  );
}

/* =========================================================
   ACCORDION STYLING HELPERS
========================================================= */
function ProgramAccordion({ children, defaultIndex = [0] }) {
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  return (
    <Accordion allowToggle allowMultiple width="100%" defaultIndex={defaultIndex}>
      {React.Children.map(children, (child, idx) => (
        <Box
          key={idx}
          border="1px solid"
          borderColor={border}
          borderRadius="18px"
          overflow="hidden"
          mb={4}
        >
          {child}
        </Box>
      ))}
    </Accordion>
  );
}

function ProgramAccordionItem({ title, children, isMobile, fontSize = 18 }) {
  const titleColor = useColorModeValue("#0F172A", "white");
  const panelColor = useColorModeValue("gray.700", "gray.300");
  const buttonBg = useColorModeValue("rgba(2,6,23,0.02)", "rgba(255,255,255,0.04)");

  return (
    <AccordionItem border="none">
      <AccordionButton
        bg={buttonBg}
        _hover={{ bg: useColorModeValue("rgba(2,6,23,0.04)", "rgba(255,255,255,0.06)") }}
        py={4}
      >
        <Box as="span" flex="1" textAlign="left" fontSize={fontSize} fontWeight="900" color={titleColor}>
          {title}
        </Box>
        <AccordionIcon />
      </AccordionButton>

      <AccordionPanel pb={5} pt={4} fontSize={{ base: 15, md: 16 }} color={panelColor} textAlign={isMobile ? "center" : "left"}>
        {children}
      </AccordionPanel>
    </AccordionItem>
  );
}

/* =========================================================
   CONTENT SECTIONS (same text as your file)
========================================================= */
function TrainingSection({ isMobile }) {
  return (
    <Box>
      <ProgramAccordion defaultIndex={[0]}>
        <ProgramAccordionItem
          title="Ateliers sur l'Équité, la Diversité et l'Inclusion (EDI)"
          isMobile={isMobile}
          fontSize={20}
        >
          Chaque année, CREATE TRAVERSAL organise des sessions de formation sur l'EDI, qui sont obligatoires pour tous les stagiaires de 
          CREATE. La formation comprend des modules synchrones et asynchrones.
          <Box mt={5}>
            <ImageSlider
              images={["N-EDI 2023.jpg", "N-EDI en 2024.jpg", "N-EDI en 2026.jpg"]}
            />
          </Box>
        </ProgramAccordionItem>

        <ProgramAccordionItem
          title="Ateliers de sondage et de tutoriels sur les systèmes VA-UAV."
          isMobile={isMobile}
          fontSize={20}
        >
          Les ateliers de sondage et de tutoriels sur les systèmes VA-UAV (véhicules autonomes-véhicules aériens sans équipage) constituent un élément vital du programme proposé. Ces ateliers sont conçus pour fournir aux stagiaires une formation complète dans la réalisation de sondages et de tutoriels sur des sujets de pointe dans le domaine des systèmes VA-UAV. L'objectif est d'équiper les stagiaires des compétences nécessaires pour explorer et comprendre les derniers développements, les défis et les opportunités dans leurs domaines de recherche respectifs.
          <Box mt={5}>
            <ImageSlider
              images={[
                "N-SurveyandTutorials_en 2024.jpg",
                "N-SurveyandTutorials_en 2025.png",
                "N-SurveyandTutorials_en 2026.jpg",
              ]}
            />
          </Box>
        </ProgramAccordionItem>

        <ProgramAccordionItem title="Nouvelle filière de cours de niveau supérieur pour les diplômés" isMobile={isMobile} fontSize={20}>
          Le programme CREATE TRAVERSAL s'apprête à introduire une gamme de cours de niveau supérieur à la pointe de la technologie. Ces cours, offerts à la fois sur le campus et à distance, couvrent des domaines critiques dans le domaine des systèmes autonomes.
        </ProgramAccordionItem>

        <ProgramAccordionItem title="Compétences professionnelles et formation" isMobile={isMobile} fontSize={20}>
          L'atelier annuel TRAVERSAL est un événement clé où les stagiaires interagissent avec des experts de l'industrie et du milieu universitaire à travers des conférences, des panels et des présentations de recherche. Cette réunion favorise les opportunités de réseautage et explore des thèmes alignés sur la portée du programme.
          <Box mt={5}>
            <ImageSlider images={["N-hackathon 2024.jpg", "N-hackathon 2025.jpg"]} />
          </Box>
        </ProgramAccordionItem>
      </ProgramAccordion>
    </Box>
  );
}

function HackathonSection({ isMobile }) {
  const textColor = useColorModeValue("gray.700", "gray.300");

  return (
    <Box>
      <Text color={textColor} fontSize={{ base: 15, md: 16 }} lineHeight={1.9} textAlign={isMobile ? "center" : "left"}>
        Le Hackathon TRAVERSAL est un événement annuel qui rassemble les étudiants de deuxième année du programme. Co-présidé par deux co-demandeurs, cet événement intensif de 48 heures met au défi les étudiants de collaborer au sein d'équipes diverses et de relever des problèmes de recherche liés à la prise de décision autonome pour les VA-UAV (véhicules autonomes-véhicules aériens sans équipage).
      </Text>

      <Box mt={6}>
        <ImageSlider images={["N-hackathon 2024.jpg", "N-hackathon 2025.jpg"]} />
      </Box>
    </Box>
  );
}

function InternshipSection({ isMobile }) {
  const textColor = useColorModeValue("gray.700", "gray.300");

  return (
    <Box>
      <Text color={textColor} fontSize={{ base: 15, md: 16 }} lineHeight={1.9} textAlign={isMobile ? "center" : "left"}>
        Les stagiaires auront l'opportunité de participer à un programme de stage en entreprise qui explore divers aspects de notre programme en collaboration avec des partenaires industriels de renom. Ces stages comprennent:
      </Text>

      <Box mt={6}>
        <ProgramAccordion defaultIndex={[]}>
          <ProgramAccordionItem title="Stages en intelligence artificielle fiable et responsable" isMobile={isMobile}>
            Plongez dans les dimensions éthiques et responsables de l'intelligence artificielle pendant votre stage. Acquérez une expérience pratique pour garantir que les systèmes d'IA sont construits en tenant compte de la fiabilité et des considérations éthiques.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="Formation en cybersécurité et en cybersécurité physique" isMobile={isMobile}>
            Rejoignez nos partenaires industriels pour une formation spécialisée en cybersécurité et en aspects cyber-physiques. Développez une expertise dans la protection des systèmes numériques et physiques contre les menaces et les vulnérabilités.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="Formation en communication, capteurs et facteurs humains" isMobile={isMobile}>
            Améliorez vos compétences en communication, en technologie des capteurs et en facteurs humains en participant à des sessions de formation offertes par nos partenaires industriels. Explorez les composantes essentielles de la communication efficace et des principes de conception centrés sur l'humain.
          </ProgramAccordionItem>
        </ProgramAccordion>
      </Box>
    </Box>
  );
}

function InternationalExchangesSection({ isMobile }) {
  const textColor = useColorModeValue("gray.700", "gray.300");

  return (
    <Box>
      <Text color={textColor} fontSize={{ base: 15, md: 16 }} lineHeight={1.9} textAlign={isMobile ? "center" : "left"}>
        Les étudiants en master et en doctorat inscrits dans notre programme auront l'occasion exceptionnelle de participer à un programme d'échanges internationaux, qui sera attribué sur une base compétitive. Cette initiative est conçue pour offrir à nos étudiants une expérience unique et enrichissante qui dépasse les frontières de la formation.
        <p />
        <br />
        Le programme TRAVERSAL étend sa portée à l'échelle mondiale grâce à une initiative d'échange international. En plus des stages en entreprise, les étudiants ont la possibilité de participer à des projets de recherche et à des collaborations avec des institutions partenaires aux États-Unis, au Brésil, en Allemagne, au Royaume-Uni et en France. Ce programme d'échange enrichit leurs perspectives, favorise l'exposition à la recherche internationale et encourage la collaboration sur des projets de pointe. Nous proposons également des stages virtuels pour nous adapter aux conditions de voyage en constante évolution. Toutes les opportunités sont offertes de manière compétitive et équitable, sous la supervision des conseillers institutionnels en équité, diversité et inclusion (EDI).
      </Text>
    </Box>
  );
}

function ConferencesAndApplySection({ isMobile }) {
  const textColor = useColorModeValue("gray.700", "gray.300");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");

  return (
    <Box>
      {/* CONFERENCES */}
      <Text color={textColor} fontSize={{ base: 15, md: 16 }} lineHeight={1.9} textAlign={isMobile ? "center" : "left"}>
        Les conférences organisées dans le cadre du programme TRAVERSAL sont conçues pour faire progresser les objectifs du programme. Ces événements réunissent des experts de premier plan, des chercheurs et des professionnels dans les domaines de la communication, de l'intelligence artificielle (IA), de la physique et de la recherche opérationnelle, tous ayant un objectif commun : promouvoir l'innovation et l'excellence au sein du programme.
      </Text>

      <Box mt={6}>
        <ImageSlider
          images={["N-conference 1.png", "N-conference 2.png"]}
          links={[
            "https://wfiot2024.iot.ieee.org/workshop-transformative-results-autonomous-vehicles-reliable-services-air-and-land-traversal",
            "https://hpsr2026.ieee-hpsr.org/workshop-network-architectures-and-intelligence-smart-mobility-and-autonomous-systems-traversal",
          ]}
        />
      </Box>

      <Box mt={{ base: 8, md: 10 }}>
        <Divider borderColor={border} />
      </Box>

      {/* HOW TO APPLY */}
      <Heading mt={{ base: 8, md: 10 }} fontSize={{ base: "xl", md: "2xl" }} letterSpacing="tight">
        COMMENT POSTULER
      </Heading>

      <Box mt={6}>
        <ProgramAccordion defaultIndex={[]}>
          <ProgramAccordionItem title="1. Préparez vos documents de candidature" isMobile={isMobile}>
            <b>Curriculum Vitae (CV):</b> Créez un CV complet mettant en avant votre parcours éducatif, votre expérience de recherche, vos réalisations académiques et toute expérience professionnelle pertinente.
            <br />
            <br />
            <b>Relevés de notes :</b> Rassemblez vos relevés de notes académiques, y compris ceux de votre premier cycle et, le cas échéant, de vos études supérieures. Assurez-vous que ces documents montrent clairement votre historique académique, vos notes et vos qualifications.
            <br />
            <br />
            <b>Travaux récemment publiés :</b> Incluez des travaux récemment publiés, tels que des articles de recherche, des publications ou des projets, mettant en valeur vos compétences académiques et de recherche. Assurez-vous de fournir le nom de votre ou vos superviseurs pour le contexte.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="2. Rassemblez votre dossier de candidature" isMobile={isMobile}>
            Regroupez tous les documents requis en un seul dossier électronique, de préférence au format PDF, pour faciliter le traitement efficace de votre candidature.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="3. Envoyez votre candidature par e-mail" isMobile={isMobile}>
            Envoyez votre dossier de candidature complet à notre adresse e-mail dédiée :

            <br />
            <b>Adresse e-mail : scvic-civic@uottawa.ca</b>
          </ProgramAccordionItem>

          <ProgramAccordionItem title="4. Objet du message" isMobile={isMobile}>
            Dans la ligne d'objet de votre e-mail, veuillez préciser "Candidature CREATE TRAVERSAL - [Votre nom complet]". Cela nous aidera à identifier et à traiter rapidement votre candidature.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="5. Date limite de candidature" isMobile={isMobile}>
            Le programme de stagiaires accepte les candidatures tout au long de l'année ; cependant, veuillez noter qu'il existe un nombre limité de places disponibles chaque année.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="6. Attendez la confirmation" isMobile={isMobile}>
            Dès réception de votre candidature, notre équipe vous en accusera réception. Soyez patient pendant que nous examinons et évaluons toutes les candidatures.
          </ProgramAccordionItem>

          <ProgramAccordionItem title="7. Entretien et évaluation" isMobile={isMobile}>
            Les candidats présélectionnés seront contactés pour des entretiens et une évaluation supplémentaire. C'est l'occasion pour nous d'en apprendre davantage sur vos qualifications, vos intérêts de recherche et votre adéquation potentielle avec le programme. Le programme TRAVERSAL promeut l'équité, la diversité et l'inclusion dans les sphères éducatives et de leadership de nos stagiaires.
          </ProgramAccordionItem>
        </ProgramAccordion>
      </Box>

      <Text
        mt={{ base: 7, md: 8 }}
        color={textColor}
        fontSize={{ base: 15, md: 16 }}
        lineHeight={1.9}
        textAlign={isMobile ? "center" : "left"}
      >
        Nous encourageons les candidatures de personnes appartenant aux quatre groupes désignés : les peuples autochtones, les minorités visibles, les personnes handicapées, les femmes et les personnes des minorités de genre, tels que définis dans les objectifs d'équité énoncés dans la Loi sur l'équité en matière d'emploi.
        <br />
        <br />
        Nous vous remercions de votre intérêt pour le programme CREATE TRAVERSAL et nous attendons avec impatience de recevoir votre candidature. Si vous avez des questions ou avez besoin d'une assistance supplémentaire pendant le processus de candidature, n'hésitez pas à nous contacter. Nous vous souhaitons bonne chance pour votre candidature et espérons vous accueillir bientôt dans notre programme.
      </Text>
    </Box>
  );
}
